import fetchWithAuth from "@/api/secureFetch";

export type ContextMeshTier = "GOOD" | "BETTER" | "BEST";
export type ContextMeshSemanticStatus = "ready" | "pending" | "failed" | "fallback";
export type ContextMeshResultSource = "current_ready" | "last_ready" | "deterministic_fallback" | "pending";
export type ContextMeshCategoryKey = "discover" | "external" | "file" | "code";
export type ContextMeshSourceCategoryKey = Exclude<ContextMeshCategoryKey, "discover">;
export type ContextMeshCategoryBand = "STRONG" | "GOOD" | "THIN" | "WEAK";
export type ContextMeshCategoryAnalysisStatus = "ready" | "pending" | "failed" | "unavailable";
export type ContextMeshSourceDiagnosticStatus = "not_connected" | "processing" | "analyzed";

export interface ContextMeshSourceDiagnostic {
    status: ContextMeshSourceDiagnosticStatus;
    connectedSourceCount: number;
}

export type ContextMeshSourceDiagnostics = Partial<Record<ContextMeshSourceCategoryKey, ContextMeshSourceDiagnostic>>;

export interface ContextMeshSemanticProvenance {
    status: ContextMeshSemanticStatus;
    isStale: boolean;
    resultSource: ContextMeshResultSource;
    snapshotVersion: number | null;
    currentSnapshotVersion: number | null;
}

export interface ContextMeshCategoryScore {
    score: number;
    band: ContextMeshCategoryBand;
    summary: string;
    method?: string | null;
}

export type ContextMeshCategoryScores = Record<ContextMeshCategoryKey, ContextMeshCategoryScore>;

const CONTEXT_MESH_NFR_FIELDS = [
    "general",
    "edge_cases",
    "latency",
    "data_formats",
    "schema_constraints",
    "error_handling",
    "system_constraints",
    "compliance"
] as const;

export type ContextMeshNfrField = typeof CONTEXT_MESH_NFR_FIELDS[number];

export interface ContextMeshDiscoverInputProvenance {
    nfrIncluded: boolean;
    nfrFieldCount: number;
    nfrFields: ContextMeshNfrField[];
}

export interface ContextMeshCategoryInputs {
    discover: ContextMeshDiscoverInputProvenance;
}

const CANONICAL_NFR_FIELDS = new Set<string>(CONTEXT_MESH_NFR_FIELDS);

export interface ContextMeshCategoryAnalysis {
    status: ContextMeshCategoryAnalysisStatus;
    isStale: boolean;
    snapshotVersion: number | null;
    scoringVersion: string | null;
    inputs: ContextMeshCategoryInputs | null;
    sourceDiagnostics?: ContextMeshSourceDiagnostics | null;
}

export interface ContextMeshEntityRef {
    type: string;
    id: string;
    label: string;
    href: string | null;
}

export interface ContextMeshRecommendation {
    recommendationId: string;
    code: string;
    message: string;
    contextCategory?: ContextMeshCategoryKey;
    priority: number;
    estimatedScoreDelta?: number | null;
    tierImpact: "none" | "unblocks_BETTER" | "unblocks_BEST";
    prerequisiteGapCodes: string[];
    entityRefs: ContextMeshEntityRef[];
    evidence: string[];
    source: "deterministic" | "semantic";
}

export type ContextMeshRecommendationStatus = "not_requested" | "analyzing" | "ready" | "failed";
export interface ContextMeshCategoryRecommendationState {
    status: ContextMeshRecommendationStatus;
    recommendation: ContextMeshRecommendation | null;
}
export type ContextMeshCategoryRecommendations = Record<ContextMeshCategoryKey, ContextMeshCategoryRecommendationState>;

export interface ContextMeshAnalysis {
    productId: number;
    tier: ContextMeshTier;
    score: number;
    readinessScore: number;
    qualityScore: number;
    categoryScores: ContextMeshCategoryScores | null;
    categoryAnalysis: ContextMeshCategoryAnalysis | null;
    rubricVersion: string;
    tierBasis: {
        candidateTier: ContextMeshTier;
        semanticFloorPassed: boolean;
        nextTier: "BETTER" | "BEST" | null;
        blockingGapCodes: string[];
    };
    semanticAnalysis: ContextMeshSemanticProvenance & {
        coverage: {
            applicableDrivers: number;
            evaluatedDrivers: number;
            totalStories: number;
            structurallyCountedStories: number;
            semanticallyEvaluatedStories: number;
            sampled: boolean;
        };
    };
    recommendations: string[];
    recommendationDetails: ContextMeshRecommendation[];
    nextBestAction: ContextMeshRecommendation | null;
    categoryRecommendations?: ContextMeshCategoryRecommendations;
}

/**
 * DataLakes intentionally omits score-bearing fields when only a
 * deterministic fallback exists. Keep that response parseable so callers can
 * present an unavailable state without ever manufacturing or exposing a
 * score.
 */
export interface ContextMeshScorelessAnalysis {
    productId: number;
    semanticAnalysis: ContextMeshSemanticProvenance;
}

export type ContextMeshAnalysisResponse = ContextMeshAnalysis | ContextMeshScorelessAnalysis;

export type ContextMeshProvenanceClassification =
    | { state: "current_canonical"; analysis: ContextMeshAnalysis }
    | { state: "prior_canonical"; analysis: ContextMeshAnalysis }
    | { state: "analyzing"; analysis: null }
    | { state: "unavailable"; analysis: null };

export interface ContextMeshErrorBody {
    code: string;
    message: string;
    requestId?: string;
    missingFields?: string[];
    recommendations?: string[];
}

export class ContextMeshApiError extends Error {
    readonly status: number;
    readonly code: string;
    readonly requestId?: string;
    readonly missingFields: string[];
    readonly recommendations: string[];

    constructor(status: number, body: ContextMeshErrorBody) {
        super(body.message);
        this.name = "ContextMeshApiError";
        this.status = status;
        this.code = body.code;
        this.requestId = body.requestId;
        this.missingFields = body.missingFields ?? [];
        this.recommendations = body.recommendations ?? [];
    }
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseError(status: number, payload: unknown): ContextMeshApiError {
    const raw = isRecord(payload) && isRecord(payload.error) ? payload.error : {};
    return new ContextMeshApiError(status, {
        code: typeof raw.code === "string" ? raw.code : "ANALYSIS_UNAVAILABLE",
        message: typeof raw.message === "string" ? raw.message : "Context analysis is temporarily unavailable.",
        requestId: typeof raw.requestId === "string" ? raw.requestId : undefined,
        missingFields: Array.isArray(raw.missingFields) ? raw.missingFields.filter((item): item is string => typeof item === "string") : [],
        recommendations: Array.isArray(raw.recommendations) ? raw.recommendations.filter((item): item is string => typeof item === "string") : []
    });
}

function requireRecord(value: unknown, field: string): Record<string, unknown> {
    if (!isRecord(value)) throw new Error(`Invalid Context Mesh response: ${field}`);
    return value;
}

function requireNumber(value: unknown, field: string): number {
    if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`Invalid Context Mesh response: ${field}`);
    return value;
}

function requireString(value: unknown, field: string): string {
    if (typeof value !== "string") throw new Error(`Invalid Context Mesh response: ${field}`);
    return value;
}

function requireBoolean(value: unknown, field: string): boolean {
    if (typeof value !== "boolean") throw new Error(`Invalid Context Mesh response: ${field}`);
    return value;
}

function requireNonNegativeInteger(value: unknown, field: string): number {
    const parsed = requireNumber(value, field);
    if (!Number.isInteger(parsed) || parsed < 0) throw new Error(`Invalid Context Mesh response: ${field}`);
    return parsed;
}

function stringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function parseEntityRef(value: unknown): ContextMeshEntityRef | null {
    if (!isRecord(value)) return null;
    if (typeof value.type !== "string" || typeof value.id !== "string" || typeof value.label !== "string") return null;
    return { type: value.type, id: value.id, label: value.label, href: typeof value.href === "string" ? value.href : null };
}

export function parseRecommendation(value: unknown): ContextMeshRecommendation | null {
    if (!isRecord(value)) return null;
    const contextCategory = value.contextCategory;
    if (
        typeof value.recommendationId !== "string" ||
        typeof value.code !== "string" ||
        typeof value.message !== "string" ||
        (contextCategory !== undefined && !["discover", "external", "file", "code"].includes(String(contextCategory))) ||
        typeof value.priority !== "number" ||
        (value.estimatedScoreDelta != null && typeof value.estimatedScoreDelta !== "number") ||
        !["none", "unblocks_BETTER", "unblocks_BEST"].includes(String(value.tierImpact)) ||
        !["deterministic", "semantic"].includes(String(value.source))
    ) return null;
    return {
        recommendationId: value.recommendationId,
        code: value.code,
        message: value.message,
        ...(contextCategory !== undefined ? { contextCategory: contextCategory as ContextMeshCategoryKey } : {}),
        priority: value.priority,
        ...(typeof value.estimatedScoreDelta === "number" ? { estimatedScoreDelta: value.estimatedScoreDelta } : {}),
        tierImpact: value.tierImpact as ContextMeshRecommendation["tierImpact"],
        prerequisiteGapCodes: stringArray(value.prerequisiteGapCodes),
        entityRefs: Array.isArray(value.entityRefs) ? value.entityRefs.map(parseEntityRef).filter((item): item is ContextMeshEntityRef => item !== null) : [],
        evidence: stringArray(value.evidence),
        source: value.source as ContextMeshRecommendation["source"]
    };
}

function parseCategoryRecommendations(value: unknown): ContextMeshCategoryRecommendations | undefined {
    if (value == null) return undefined;
    const root = requireRecord(value, "categoryRecommendations");
    const result = {} as ContextMeshCategoryRecommendations;
    for (const category of ["discover", "external", "file", "code"] as const) {
        const row = requireRecord(root[category], `categoryRecommendations.${category}`);
        const status = requireString(row.status, `categoryRecommendations.${category}.status`) as ContextMeshRecommendationStatus;
        if (!["not_requested", "analyzing", "ready", "failed"].includes(status)) {
            throw new Error(`Invalid Context Mesh response: categoryRecommendations.${category}.status`);
        }
        const recommendation = row.recommendation == null ? null : parseRecommendation(row.recommendation);
        if ((status === "ready") !== (recommendation !== null)) {
            throw new Error(`Invalid Context Mesh response: categoryRecommendations.${category}.recommendation`);
        }
        result[category] = { status, recommendation };
    }
    return result;
}

function parseCategoryScore(value: unknown, field: string): ContextMeshCategoryScore {
    const row = requireRecord(value, field);
    const score = requireNumber(row.score, `${field}.score`);
    const band = requireString(row.band, `${field}.band`) as ContextMeshCategoryBand;
    if (score < 0 || score > 100 || !["STRONG", "GOOD", "THIN", "WEAK"].includes(band)) {
        throw new Error(`Invalid Context Mesh response: ${field}`);
    }
    return {
        score,
        band,
        summary: requireString(row.summary, `${field}.summary`),
        method: typeof row.method === "string" && row.method.trim() ? row.method.trim() : null
    };
}

function parseCategoryScores(value: unknown): ContextMeshCategoryScores | null {
    // Category scores are optional while the independent category analysis is
    // pending, unavailable, or rolling out to the target environment.
    if (value == null) return null;
    const rows = requireRecord(value, "categoryScores");
    return {
        discover: parseCategoryScore(rows.discover, "categoryScores.discover"),
        external: parseCategoryScore(rows.external, "categoryScores.external"),
        file: parseCategoryScore(rows.file, "categoryScores.file"),
        code: parseCategoryScore(rows.code, "categoryScores.code")
    };
}

function parseDiscoverInputProvenance(value: unknown): ContextMeshDiscoverInputProvenance | null {
    if (!isRecord(value)) return null;
    if (
        typeof value.nfrIncluded !== "boolean" ||
        typeof value.nfrFieldCount !== "number" ||
        !Number.isInteger(value.nfrFieldCount) ||
        value.nfrFieldCount < 0 ||
        !Array.isArray(value.nfrFields) ||
        value.nfrFields.some(field => typeof field !== "string" || !CANONICAL_NFR_FIELDS.has(field))
    ) return null;

    const nfrFields = value.nfrFields as ContextMeshNfrField[];
    if (
        value.nfrFieldCount !== nfrFields.length ||
        value.nfrIncluded !== (nfrFields.length > 0) ||
        nfrFields.some((field, index) => index > 0 && nfrFields[index - 1] >= field)
    ) return null;

    return {
        nfrIncluded: value.nfrIncluded,
        nfrFieldCount: value.nfrFieldCount,
        nfrFields
    };
}

function parseCategoryInputs(value: unknown): ContextMeshCategoryInputs | null {
    if (!isRecord(value)) return null;
    const discover = parseDiscoverInputProvenance(value.discover);
    return discover === null ? null : { discover };
}

export function parseSourceDiagnostic(
    value: unknown,
    field = "sourceDiagnostic"
): ContextMeshSourceDiagnostic | null {
    if (value == null) return null;
    try {
        const row = requireRecord(value, field);
        const status = requireString(row.status, `${field}.status`) as ContextMeshSourceDiagnosticStatus;
        if (!["not_connected", "processing", "analyzed"].includes(status)) {
            throw new Error(`Invalid Context Mesh response: ${field}.status`);
        }
        const diagnostic = {
            status,
            connectedSourceCount: requireNonNegativeInteger(row.connectedSourceCount, `${field}.connectedSourceCount`)
        };
        const isEmptyDisconnected = status === "not_connected" && diagnostic.connectedSourceCount === 0;
        const isConnected = status !== "not_connected" && diagnostic.connectedSourceCount > 0;
        if (!isEmptyDisconnected && !isConnected) {
            throw new Error(`Invalid Context Mesh response: ${field}`);
        }
        return diagnostic;
    } catch {
        // Source diagnostics are additive rollout metadata. A malformed
        // optional diagnostic must never invalidate an otherwise canonical
        // score or detail report.
        return null;
    }
}

function parseSourceDiagnostics(value: unknown): ContextMeshSourceDiagnostics | null {
    if (value == null) return null;
    try {
        const rows = requireRecord(value, "categoryAnalysis.sourceDiagnostics");
        const diagnostics: ContextMeshSourceDiagnostics = {};
        for (const category of ["external", "file", "code"] as const) {
            if (!(category in rows)) continue;
            const diagnostic = parseSourceDiagnostic(
                rows[category],
                `categoryAnalysis.sourceDiagnostics.${category}`
            );
            if (diagnostic) diagnostics[category] = diagnostic;
        }
        return Object.keys(diagnostics).length > 0 ? diagnostics : null;
    } catch {
        // Diagnostics are additive. Reject a malformed outer map without
        // hiding the canonical category scores carried by the same response.
        return null;
    }
}

export function parseCategoryAnalysis(value: unknown): ContextMeshCategoryAnalysis | null {
    // The category analyzer is an additive contract. Older DataLakes responses
    // have deterministic categoryScores but no provenance, so treat them as
    // unavailable rather than presenting those values as canonical scores.
    if (value == null) return null;
    const row = requireRecord(value, "categoryAnalysis");
    const status = requireString(row.status, "categoryAnalysis.status") as ContextMeshCategoryAnalysisStatus;
    if (!["ready", "pending", "failed", "unavailable"].includes(status)) {
        throw new Error("Invalid Context Mesh response: categoryAnalysis.status");
    }
    const snapshotVersion = row.snapshotVersion == null
        ? null
        : requireNumber(row.snapshotVersion, "categoryAnalysis.snapshotVersion");
    if (snapshotVersion !== null && !Number.isInteger(snapshotVersion)) {
        throw new Error("Invalid Context Mesh response: categoryAnalysis.snapshotVersion");
    }
    const scoringVersion = typeof row.scoringVersion === "string" && row.scoringVersion.trim()
        ? row.scoringVersion
        : null;
    const isStale = requireBoolean(row.isStale, "categoryAnalysis.isStale");
    const hasCurrentV2InputProvenance = status === "ready"
        && !isStale
        && snapshotVersion !== null
        && scoringVersion === "context-mesh-category-v2";
    return {
        status,
        isStale,
        snapshotVersion,
        scoringVersion,
        inputs: hasCurrentV2InputProvenance ? parseCategoryInputs(row.inputs) : null,
        sourceDiagnostics: parseSourceDiagnostics(row.sourceDiagnostics)
    };
}

const unavailableCategoryState = (): ContextMeshCategoryAnalysis => ({
    status: "unavailable",
    isStale: false,
    snapshotVersion: null,
    scoringVersion: null,
    inputs: null,
    sourceDiagnostics: null
});

function parseCategoryState(
    analysisValue: unknown,
    scoresValue: unknown
): Pick<ContextMeshAnalysis, "categoryAnalysis" | "categoryScores"> {
    // Missing category provenance is valid during a rolling deployment. Never
    // expose scores from that legacy shape because they may be deterministic.
    if (analysisValue == null) {
        return { categoryAnalysis: null, categoryScores: null };
    }

    let categoryAnalysis: ContextMeshCategoryAnalysis | null;
    let categoryScores: ContextMeshCategoryScores | null;
    try {
        categoryAnalysis = parseCategoryAnalysis(analysisValue);
        categoryScores = parseCategoryScores(scoresValue);
    } catch {
        return { categoryAnalysis: unavailableCategoryState(), categoryScores: null };
    }

    if (categoryAnalysis === null) {
        return { categoryAnalysis: unavailableCategoryState(), categoryScores: null };
    }

    const hasPersistedSnapshot = Number.isInteger(categoryAnalysis.snapshotVersion);
    const isCurrentCanonical = categoryAnalysis.status === "ready"
        && !categoryAnalysis.isStale
        && hasPersistedSnapshot
        && categoryScores !== null;
    const isRetainedCanonical = ["pending", "failed"].includes(categoryAnalysis.status)
        && categoryAnalysis.isStale
        && hasPersistedSnapshot
        && categoryScores !== null;
    if (isCurrentCanonical || isRetainedCanonical) {
        return { categoryAnalysis, categoryScores };
    }

    const isValidEmptyState = categoryScores === null
        && categoryAnalysis.snapshotVersion === null
        && !categoryAnalysis.isStale
        && ["pending", "failed", "unavailable"].includes(categoryAnalysis.status);
    if (isValidEmptyState) {
        return { categoryAnalysis, categoryScores: null };
    }

    // Treat the pair atomically: contradictory provenance must not survive as
    // a misleading ready/pending label after its score payload is rejected.
    return { categoryAnalysis: unavailableCategoryState(), categoryScores: null };
}

function parseSnapshotVersion(value: unknown, field: string): number | null {
    if (value == null) return null;
    const version = requireNumber(value, field);
    if (!Number.isInteger(version) || version < 0) {
        throw new Error(`Invalid Context Mesh response: ${field}`);
    }
    return version;
}

function parseSemanticProvenance(value: unknown): ContextMeshSemanticProvenance {
    const semantic = requireRecord(value, "semanticAnalysis");
    const status = requireString(semantic.status, "semanticAnalysis.status") as ContextMeshSemanticStatus;
    const resultSource = requireString(semantic.resultSource, "semanticAnalysis.resultSource") as ContextMeshResultSource;
    if (!["ready", "pending", "failed", "fallback"].includes(status)) {
        throw new Error("Invalid Context Mesh response: semantic status");
    }
    if (!["current_ready", "last_ready", "deterministic_fallback", "pending"].includes(resultSource)) {
        throw new Error("Invalid Context Mesh response: result source");
    }
    return {
        status,
        isStale: requireBoolean(semantic.isStale, "semanticAnalysis.isStale"),
        resultSource,
        snapshotVersion: parseSnapshotVersion(semantic.snapshotVersion, "semanticAnalysis.snapshotVersion"),
        currentSnapshotVersion: parseSnapshotVersion(semantic.currentSnapshotVersion, "semanticAnalysis.currentSnapshotVersion")
    };
}

function hasCompleteScorePayload(root: Record<string, unknown>): boolean {
    return typeof root.tier === "string"
        && typeof root.score === "number"
        && Number.isFinite(root.score)
        && typeof root.readinessScore === "number"
        && Number.isFinite(root.readinessScore)
        && typeof root.qualityScore === "number"
        && Number.isFinite(root.qualityScore)
        && isRecord(root.tierBasis)
        && typeof root.rubricVersion === "string"
        && Array.isArray(root.recommendationDetails);
}

export function classifyContextMeshProvenance(
    response: ContextMeshAnalysisResponse
): ContextMeshProvenanceClassification {
    const semantic = response.semanticAnalysis;
    const hasScores = "score" in response;
    const snapshotVersion = semantic.snapshotVersion;
    const currentSnapshotVersion = semantic.currentSnapshotVersion;
    const hasValidSnapshots = Number.isInteger(snapshotVersion)
        && Number.isInteger(currentSnapshotVersion)
        && snapshotVersion! >= 0
        && currentSnapshotVersion! >= 0;

    if (
        hasScores
        && semantic.status === "ready"
        && semantic.resultSource === "current_ready"
        && !semantic.isStale
        && hasValidSnapshots
        && snapshotVersion === currentSnapshotVersion
    ) {
        return { state: "current_canonical", analysis: response };
    }
    if (
        hasScores
        && semantic.resultSource === "last_ready"
        && semantic.isStale
        && hasValidSnapshots
        && snapshotVersion! < currentSnapshotVersion!
    ) {
        return { state: "prior_canonical", analysis: response };
    }
    if (semantic.status === "pending") {
        return { state: "analyzing", analysis: null };
    }
    return { state: "unavailable", analysis: null };
}

export function parseContextMeshAnalysis(payload: unknown): ContextMeshAnalysisResponse {
    const root = requireRecord(payload, "root");
    const productId = requireNumber(root.productId, "productId");
    const semanticProvenance = parseSemanticProvenance(root.semanticAnalysis);

    // During the scoreless-fallback rollout, accept both the old complete
    // fallback response and the new response that omits or nulls score fields.
    // Reduce both shapes to provenance immediately so deterministic scores and
    // recommendations can never materialize downstream.
    if (semanticProvenance.resultSource === "deterministic_fallback") {
        return { productId, semanticAnalysis: semanticProvenance };
    }
    // Accepted matrix work has no published score. Only its exact pending
    // provenance can use this scoreless contract; discard unfinished values.
    if (semanticProvenance.resultSource === "pending") {
        if (semanticProvenance.status !== "pending"
            || semanticProvenance.isStale
            || semanticProvenance.snapshotVersion !== null
            || semanticProvenance.currentSnapshotVersion === null) {
            throw new Error("Invalid Context Mesh response: pending provenance");
        }
        return { productId, semanticAnalysis: semanticProvenance };
    }
    // No other provenance may bypass the full score contract.
    if (!hasCompleteScorePayload(root)) {
        throw new Error("Invalid Context Mesh response: score payload");
    }

    const tierBasis = requireRecord(root.tierBasis, "tierBasis");
    const semantic = requireRecord(root.semanticAnalysis, "semanticAnalysis");
    const coverage = requireRecord(semantic.coverage, "semanticAnalysis.coverage");
    const categoryState = parseCategoryState(root.categoryAnalysis, root.categoryScores);
    const tier = requireString(root.tier, "tier") as ContextMeshTier;
    const candidateTier = requireString(tierBasis.candidateTier, "tierBasis.candidateTier") as ContextMeshTier;
    if (!["GOOD", "BETTER", "BEST"].includes(tier) || !["GOOD", "BETTER", "BEST"].includes(candidateTier)) throw new Error("Invalid Context Mesh response: tier");
    if (!Array.isArray(root.recommendationDetails)) throw new Error("Invalid Context Mesh response: recommendationDetails");
    const parsedDetails = root.recommendationDetails.map(parseRecommendation);
    if (parsedDetails.some(item => item === null)) throw new Error("Invalid Context Mesh response: recommendationDetails item");
    const details = parsedDetails as ContextMeshRecommendation[];
    const nextBestAction = root.nextBestAction == null ? null : parseRecommendation(root.nextBestAction);
    if (root.nextBestAction != null && nextBestAction == null) throw new Error("Invalid Context Mesh response: nextBestAction");
    const categoryRecommendations = parseCategoryRecommendations(root.categoryRecommendations);
    return {
        productId,
        tier,
        score: requireNumber(root.score, "score"),
        readinessScore: requireNumber(root.readinessScore, "readinessScore"),
        qualityScore: requireNumber(root.qualityScore, "qualityScore"),
        ...categoryState,
        rubricVersion: requireString(root.rubricVersion, "rubricVersion"),
        tierBasis: {
            candidateTier,
            semanticFloorPassed: requireBoolean(tierBasis.semanticFloorPassed, "tierBasis.semanticFloorPassed"),
            nextTier: tierBasis.nextTier === "BETTER" || tierBasis.nextTier === "BEST" ? tierBasis.nextTier : null,
            blockingGapCodes: stringArray(tierBasis.blockingGapCodes)
        },
        semanticAnalysis: {
            ...semanticProvenance,
            coverage: {
                applicableDrivers: requireNumber(coverage.applicableDrivers, "coverage.applicableDrivers"),
                evaluatedDrivers: requireNumber(coverage.evaluatedDrivers, "coverage.evaluatedDrivers"),
                totalStories: requireNumber(coverage.totalStories, "coverage.totalStories"),
                structurallyCountedStories: requireNumber(coverage.structurallyCountedStories, "coverage.structurallyCountedStories"),
                semanticallyEvaluatedStories: requireNumber(coverage.semanticallyEvaluatedStories, "coverage.semanticallyEvaluatedStories"),
                sampled: requireBoolean(coverage.sampled, "coverage.sampled")
            }
        },
        recommendations: stringArray(root.recommendations),
        recommendationDetails: details,
        nextBestAction,
        ...(categoryRecommendations ? { categoryRecommendations } : {})
    };
}

export async function scheduleContextMeshCategoryRecommendations(
    token: string,
    target: number | { contextMeshId: string },
    retryFailed = false
): Promise<void> {
    const path = typeof target === "number"
        ? `/api/products/${target}/context-analysis/recommendations`
        : `/api/context-meshes/${target.contextMeshId}/context-analysis/recommendations`;
    const response = await fetchWithAuth(path, token, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ retryFailed })
    });
    if (!response.ok) {
        throw new ContextMeshApiError(response.status, {
            code: "RECOMMENDATIONS_UNAVAILABLE",
            message: "Recommendations are temporarily unavailable."
        });
    }
}

export async function fetchContextMeshAnalysis(token: string, productId: number): Promise<ContextMeshAnalysisResponse> {
    let response: Response;
    try {
        response = await fetchWithAuth(`/api/products/${productId}/context-analysis`, token, { headers: { Accept: "application/json" } });
    } catch {
        throw new ContextMeshApiError(503, { code: "ANALYSIS_UNAVAILABLE", message: "Context analysis is temporarily unavailable." });
    }
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new ContextMeshApiError(response.status || 503, { code: "INVALID_RESPONSE", message: "The Context Mesh service returned an invalid response." });
    }
    if (!response.ok) throw parseError(response.status, payload);
    try {
        return parseContextMeshAnalysis(payload);
    } catch {
        throw new ContextMeshApiError(503, { code: "INVALID_RESPONSE", message: "The Context Mesh service returned an invalid response." });
    }
}

export async function fetchWorkspaceContextMeshAnalysis(
    token: string,
    contextMeshId: string
): Promise<ContextMeshAnalysisResponse> {
    let response: Response;
    try {
        response = await fetchWithAuth(
            `/api/context-meshes/${contextMeshId}/context-analysis`,
            token,
            { headers: { Accept: "application/json" } }
        );
    } catch {
        throw new ContextMeshApiError(503, { code: "ANALYSIS_UNAVAILABLE", message: "Context analysis is temporarily unavailable." });
    }
    let payload: unknown;
    try {
        payload = await response.json();
    } catch {
        throw new ContextMeshApiError(response.status || 503, { code: "INVALID_RESPONSE", message: "The Context Mesh service returned an invalid response." });
    }
    if (!response.ok) throw parseError(response.status, payload);
    try {
        const wrapper = requireRecord(payload, "root");
        if (requireString(wrapper.contextMeshId, "contextMeshId") !== contextMeshId) {
            throw new Error("Mismatched Context Mesh identity");
        }
        const analysis = requireRecord(wrapper.analysis, "analysis");
        // The shared console keys its existing view model by a numeric Product
        // id. A standalone mesh has no Product; zero is an internal adapter
        // value and is never sent back to an API.
        return parseContextMeshAnalysis({ ...analysis, productId: 0 });
    } catch {
        throw new ContextMeshApiError(503, { code: "INVALID_RESPONSE", message: "The Context Mesh service returned an invalid response." });
    }
}
